import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormColorPicker from "../../components/form/FormColorPicker";
import FormDatePicker from "../../components/form/formDatePicker/FormDatePicker";
import SingleFormDatePicker from "../../components/form/formDatePicker/SingleFormDatePicker";
import FormDropdown from "../../components/form/FormDropdown";
import FormDurationPicker from "../../components/form/FormDurationPicker";
import FormIconPicker from "../../components/form/FormIconPicker";
import FormTextField from "../../components/form/FormTextField";
import InspectorBottomBox from "../../components/inspector/InspectorBottomBox";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorHeader from "../../components/inspector/InspectorHeader";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import TabLock from "../../components/tabLock";
import ToolbarTextButton from "../../components/toolbar/ToolbarTextButton";
// const swatchColorOptions = ["red","peach","orange","yellow","mint","apple","lightTeal","darkTeal","lightBlue","denim","midBlue","darkBlue","darkPurple","lightPurple","pastelPink","fuschia","lightPink","darkPink","grey","mono"];
import calendarMarkerIcons from "../../constants/calendarMarkerIcons";
import swatchColorOptions from "../../constants/colorOptions";
import { DATE_FIELD_END, DATE_FIELD_START } from "../../constants/dateFields";
import PubSubTopics from "../../constants/pubSubTopics";
import { appStateOperations } from "../../modules/appState";
import { calendarMarkerOperations } from "../../modules/calendarMarkers";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import {
  precisionForTimespan,
  timespanForPrecisionAndUnits,
  unitsForTimespan
} from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { Precision, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";
import userInputValidators from "../../utils/userInputValidators";
import selectors from "./selectors";

const colorNames = ["iconMono"];

const iconOptions = () => {
  let options = [];
  calendarMarkerIcons.forEach(i => {
    colorNames.forEach(c => {
      options.push({ color: c, icon: i });
    });
  });
  return options;
};

const repeatUnitOptions = [
  { label: "years", value: String(Precision.Year) },
  { label: "months", value: String(Precision.Month) },
  { label: "weeks", value: String(Precision.Week) },
  { label: "days", value: String(Precision.Day) },
  { label: "hours", value: String(Precision.Hour) },
  { label: "minutes", value: String(Precision.Minute) },
  { label: "seconds", value: String(Precision.Second) }
];

const getCollapseIcon = (onCollapseClick, showCollapseLeft) =>
  onCollapseClick ? (
    showCollapseLeft ? (
      <ToolbarTextButton icon="leftArrow" text="Markers" navigateBackStyle={true} onClick={onCollapseClick} />
    ) : (
      <Icon
        icon="rightArrow"
        className="inspectorCollapseArrow"
        color="iconMono"
        onClick={onCollapseClick}
        tooltip="Hide Inspector Panel"
      />
    )
  ) : (
    <div className="spacer" style={{ flex: 1 }} />
  );

const getScrollToDateIcon = date => {
  // TO DO: Add a menu to choose occurence to scroll to
  return isDateValid(date) ? (
    <div className="inspectorScrollToItem">
      <Icon
        icon="sidebarScrollToDate"
        color="iconMono"
        onClick={() => scrollToMarker(date)}
        tooltip="Scroll to marker"
      />
    </div>
  ) : null;
};

const scrollToMarker = date => {
  PubSub.publish(PubSubTopics.scrollToDate, date);
};

const renderTopGroup = useProps => {
  const isIos = preprocessorQuery.isIos();
  const markerIds = useProps.markerIds.toArray();
  const displayIcon = useProps.markerData.icon ? useProps.markerData.icon : "noMarker";
  const displayLabel = useProps.markerData.label;

  let collapseIcon = getCollapseIcon(useProps.onCollapseClick, isIos);
  let scrollToDateIcon = getScrollToDateIcon(useProps.markerData.startDate);

  return (
    <InspectorGroup>
      <InspectorHeader>
        {isIos ? (
          <Fragment>
            {collapseIcon}
            {scrollToDateIcon}
          </Fragment>
        ) : (
          <Fragment>
            <div className="inspectorScrollToItem">
              {scrollToDateIcon}
              <span>Calendar Marker</span>
            </div>
            {collapseIcon}
          </Fragment>
        )}
      </InspectorHeader>
      <InspectorRow label="Label">
        <InspectorField>
          <FormTextField
            multiline={false}
            value={displayLabel}
            autoFocusOnEmpty={false}
            onChange={label => useProps.onLabelChange(markerIds, label)}
            placeholder="Calendar Marker Label"
          />
        </InspectorField>
      </InspectorRow>
      <InspectorRow label="Display">
        <InspectorField label="Color">
          <FormColorPicker
            value={useProps.markerData.color}
            options={swatchColorOptions}
            id="markerInspectorColorPicker"
            onChange={color => useProps.onColorChange(markerIds, color)}
          />
        </InspectorField>
        <InspectorField label="Icon">
          <FormIconPicker
            value={{ icon: displayIcon, color: "iconMono" }}
            options={iconOptions()}
            id="markerInspectorIconPicker"
            onChange={opt => useProps.onIconChange(markerIds, opt)}
          />
        </InspectorField>
      </InspectorRow>
      <InspectorRow label=" ">
        <InspectorField>
          <FormCheckbox
            checkboxState={useProps.markerData.drawOnTimeline}
            label="Draw on Timeline"
            onChange={newState => useProps.onDrawOnTimelineChange(markerIds, newState)}
          />
        </InspectorField>
      </InspectorRow>
    </InspectorGroup>
  );
};

const renderDatesGroup = useProps => {
  const markerIds = useProps.markerIds.toArray();
  const onStartChange = date => useProps.onStartChange(markerIds, date);
  const onEndChange = date => useProps.onEndChange(markerIds, date);
  const onBulkDateChange = date => useProps.onBulkDateChange(markerIds, date);
  const getDatePicker = isEndDate => (
    <FormDatePicker
      isDual={true}
      allowUncertainDates={false}
      startDate={useProps.markerData.startDate}
      endDate={useProps.markerData.endDate}
      duration={useProps.markerData.duration}
      autoAdjustEndDate={true}
      calendar={useProps.calendar}
      onStartDateChange={onStartChange}
      onEndDateChange={onEndChange}
      onBulkDateChange={onBulkDateChange}
      selectedDateField={isEndDate ? DATE_FIELD_END : DATE_FIELD_START}
    />
  );
  return (
    <InspectorGroup label="Date">
      <InspectorRow label="Start">
        <InspectorField>{getDatePicker(false)}</InspectorField>
      </InspectorRow>
      <InspectorRow label="End">
        <InspectorField>{getDatePicker(true)}</InspectorField>
      </InspectorRow>
      <InspectorRow label="Duration">
        <InspectorField>
          <FormDurationPicker
            value={useProps.markerData.duration}
            calendar={useProps.calendar}
            onChange={span => useProps.onDurationChange(markerIds, span)}
            validator={userInputValidators.itemDuration}
          />
        </InspectorField>
      </InspectorRow>
    </InspectorGroup>
  );
};

const renderRepeatGroup = useProps => {
  const markerIds = useProps.markerIds.toArray();
  const repeatPrecision = precisionForTimespan(useProps.markerData.repeatFrequency);
  const repeatUnits = unitsForTimespan(useProps.markerData.repeatFrequency, repeatPrecision);
  return (
    <InspectorGroup
      label="Repeat"
      showCheckbox={true}
      isChecked={useProps.markerData.repeat}
      onChange={value => useProps.onRepeatChange(markerIds, value)}
      disabled={!isDateValid(useProps.markerData.startDate)}
    >
      <InspectorRow label="Every">
        <InspectorField>
          <FormTextField
            multiline={false}
            numeric={true}
            isInteger={true}
            value={String(repeatUnits)}
            onChange={units => useProps.onRepeatFrequencyChange(markerIds, parseInt(units), repeatPrecision)}
          />
        </InspectorField>
        <InspectorField>
          <FormDropdown
            options={repeatUnitOptions}
            allowSearch={false}
            value={String(repeatPrecision)}
            onChange={precision => useProps.onRepeatFrequencyChange(markerIds, repeatUnits, parseInt(precision))}
            disabled={repeatUnits === 0}
          />
        </InspectorField>
      </InspectorRow>
      <InspectorRow label="Until">
        <InspectorField>
          <SingleFormDatePicker
            value={useProps.markerData.repeatUntil}
            calendar={useProps.calendar}
            onChange={date => useProps.onRepeatUntilChange(markerIds, date)}
          />
        </InspectorField>
      </InspectorRow>
    </InspectorGroup>
  );
};

const CalendarMarkerInspector = props => {
  return (
    <div className="inspector">
      <TabLock autoFocus={false}>
        <div className="inspectorHeightAutoGrowArea">
          {renderTopGroup(props)}
          {renderDatesGroup(props)}
          {renderRepeatGroup(props)}
        </div>
        <InspectorBottomBox
          itemIds={props.markerIds}
          onDeleteItems={props.onDelete}
          deleteLabel="Delete Marker"
          tooltip="Remove calendar marker from timeline"
        />
      </TabLock>
    </div>
  );
};

CalendarMarkerInspector.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  markerIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  markerData: PropTypeShapes.inspectorItem,
  // Dispatch actions
  onColorChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onIconChange: PropTypes.func.isRequired,
  onDrawOnTimelineChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCollapseClick: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const inspectorData = selectors.getInspectorMarkerData(state.present);
  return {
    markerIds: inspectorData.markerIds,
    markerData: inspectorData.markerData,
    calendar: calendarSelectors.getCalendar(state.present)
  };
};

const readonlyActions = {};

const editingActions = {
  onLabelChange: calendarMarkerOperations.editLabel,
  onColorChange: calendarMarkerOperations.editColor,
  onIconChange: (markerIds, pair) => {
    if (pair.icon === "noMarker") {
      return calendarMarkerOperations.editIcon(markerIds, null);
    } else {
      return calendarMarkerOperations.editIcon(markerIds, pair.icon);
    }
  },
  onDrawOnTimelineChange: calendarMarkerOperations.setDrawOnTimeline,
  onStartChange: (markerIds, date) => {
    return calendarMarkerOperations.setStartDate(markerIds, date.timestamp, date.precision);
  },
  onEndChange: (markerIds, date) => {
    return calendarMarkerOperations.setEndDate(markerIds, date.timestamp, date.precision);
  },
  onBulkDateChange: (markerIds, value) => {
    return calendarMarkerOperations.setStartAndEndDates(markerIds, value);
  },
  onDurationChange: (itemIds, span) => {
    return calendarMarkerOperations.setDuration(itemIds, span.toJS());
  },
  onRepeatChange: calendarMarkerOperations.setRepeat,
  onRepeatFrequencyChange: (markerIds, units, precision) => {
    const span = timespanForPrecisionAndUnits(precision, units);
    return calendarMarkerOperations.setRepeatFrequency(markerIds, span.toJS());
  },
  onRepeatUntilChange: (markerIds, date) => {
    return calendarMarkerOperations.setRepeatUntil(markerIds, date.timestamp, date.precision);
  },
  onDelete: markerIds => {
    return calendarMarkerOperations.deleteMarkers(markerIds);
  },
  onCollapseClick: () => {
    let action = null;
    // #!if iOS === true
// action = appStateOperations.closeIosInspectorPanel();
    // #!else
    action = appStateOperations.hideRightPanel();
    // #!endif
    return action;
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CalendarMarkerInspector);
